import {FC, KeyboardEventHandler} from 'react';
import {Controller, FormProvider, SubmitHandler, useForm} from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AccordionDetails,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import {find, includes} from 'lodash';
import {useClickOutside} from '../../../hooks/useClickOutside';
import {FilterNameType} from '../../../shared/types/filtersSet.types';
import {filterServiceRequestsBy} from './filters.data';
import {DictSearchField} from './DictSearchField';
import {PredefinedField} from './PredefinedField';

interface Props {
  onChange?: (values: IReportFilterValue) => void;
  selectedFilters?: IReportFilterValue[];
}

export interface IReportFilterValue {
  filterBy: FilterNameType | string;
  value: string | Record<string, any>;
}

const DICT_SEARCH: string[] = ['city', 'region', 'street', 'workerUid'];
const PREDEFINED: string[] = ['taskLocationCode', 'serviceType'];

export const ReportFilter: FC<Props> = ({onChange, selectedFilters = []}) => {
  const form = useForm<IReportFilterValue>({
    mode: 'onChange',
    defaultValues: {
      value: '',
      filterBy: '',
    },
  });

  const {
    control,
    watch,
    formState: {isValid},
    handleSubmit,
    setValue,
    getValues,
  } = form;

  const {ref, isComponentVisible, setIsComponentVisible} = useClickOutside(false);

  const filterByWatch = watch('filterBy');

  const resetFilters = () => {
    setValue('filterBy', '', {shouldValidate: true});
    setValue('value', '', {shouldValidate: true});
  };

  const handleExpand = () => {
    setIsComponentVisible((prev) => !prev);
    if (!isComponentVisible) resetFilters();
  };

  const cancelFilter = () => {
    resetFilters();
  };

  const addFilter: SubmitHandler<IReportFilterValue> = (data) => {
    onChange?.(data);
    resetFilters();
    setIsComponentVisible(false);
  };

  const keyHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.code === 'Escape') setValue('value', '', {shouldValidate: true});
    if (e.code === 'Enter') getValues('value') && handleSubmit(addFilter)();
  };

  const filterByOptions = filterServiceRequestsBy.filter((item) => !find(selectedFilters, ['filterBy', item[1]]));

  console.log(filterByWatch);

  return (
    <FormProvider {...form}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          height: '32px',
          position: 'relative',
          zIndex: 5,
        }}
      >
        <Stack position="absolute" direction="row" width="100%" spacing={2}>
          <Accordion
            onChange={handleExpand}
            expanded={isComponentVisible}
            sx={{width: '100%'}}
            disableGutters
            elevation={0}
            disabled={!filterByOptions.length}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <AddIcon sx={{mr: 1}} fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold">
                Новый фильтр
              </Typography>
            </AccordionSummary>
            <AccordionDetails ref={ref}>
              <Controller
                control={control}
                name="filterBy"
                render={({field: {onChange, value}}) => (
                  <FormControl sx={{mb: 1}} fullWidth>
                    <InputLabel size="small" id="filterBy-select-label">
                      Тип фильтра
                    </InputLabel>
                    <Select
                      labelId="filterBy-select-label"
                      id="filterBy-select"
                      value={value}
                      label="Тип фильтра"
                      onChange={onChange}
                      size="small"
                    >
                      {filterByOptions.map(([label, value]) => (
                        <MenuItem key={label + value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              {filterByWatch && (
                <>
                  <Controller
                    control={control}
                    name="value"
                    render={({field: {onChange, value, ref}, fieldState: {error}}) => (
                      <>
                        {includes(DICT_SEARCH, filterByWatch) ? (
                          <DictSearchField filterByWatch={filterByWatch} />
                        ) : includes(PREDEFINED, filterByWatch) ? (
                          <PredefinedField filterByWatch={filterByWatch} />
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            onChange={onChange}
                            value={value}
                            inputRef={ref}
                            error={!!error}
                            helperText={error ? error.message : ''}
                            onKeyDown={keyHandler}
                          />
                        )}
                        <Stack sx={{justifyContent: 'flex-end', mt: 1}} direction="row" spacing={2}>
                          <Button sx={{fontWeight: 'bold'}} size="small" color="error" onClick={() => cancelFilter()}>
                            Отмена
                          </Button>
                          <Button
                            sx={{fontWeight: 'bold'}}
                            size="small"
                            disabled={!value || !isValid}
                            onClick={handleSubmit(addFilter)}
                          >
                            Применить
                          </Button>
                        </Stack>
                      </>
                    )}
                  />
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Stack>
    </FormProvider>
  );
};
